/**
 * React Error Boundary for catching component errors
 */

import { AlertTriangle, RefreshCw } from "lucide-react";
import { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

interface Props {
	children: ReactNode;
	fallback?: ReactNode;
}

interface State {
	hasError: boolean;
	error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: unknown) {
		if (import.meta.env.DEV) {
			console.error("ErrorBoundary caught error:", error, errorInfo);
		}

		// Lazy-load Sentry to keep it out of the initial bundle
		import("@sentry/tanstackstart-react").then((Sentry) => {
			Sentry.captureException(error, {
				contexts: { react: { componentStack: errorInfo } },
			});
		});
	}

	handleReset = () => {
		window.location.reload();
	};

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) {
				return this.props.fallback;
			}

			return <ErrorFallback error={this.state.error} onReset={this.handleReset} />;
		}

		return this.props.children;
	}
}

interface ErrorFallbackProps {
	error?: Error;
	onReset: () => void;
}

function ErrorFallback({ error, onReset }: ErrorFallbackProps) {
	return (
		<div className="flex min-h-screen items-center justify-center p-4">
			<Card className="w-full max-w-lg">
				<CardHeader>
					<div className="flex items-center gap-3">
						<div className="flex size-10 items-center justify-center rounded-full bg-destructive/10">
							<AlertTriangle className="size-5 text-destructive" />
						</div>
						<CardTitle>Something went wrong</CardTitle>
					</div>
				</CardHeader>
				<CardContent className="space-y-3">
					<p className="text-sm text-muted-foreground">
						An unexpected error occurred. Please try refreshing the page or contact support if the
						problem persists.
					</p>
					{import.meta.env.DEV && error && (
						<details className="rounded-md bg-muted p-3">
							<summary className="cursor-pointer text-sm font-medium">
								Error Details (Dev Only)
							</summary>
							<pre className="mt-2 overflow-auto text-xs">
								{error.message}
								{"\n\n"}
								{error.stack}
							</pre>
						</details>
					)}
				</CardContent>
				<CardFooter>
					<Button onClick={onReset} className="w-full">
						<RefreshCw className="mr-2 size-4" />
						Try Again
					</Button>
				</CardFooter>
			</Card>
		</div>
	);
}
